<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { Input, Label, Button, Icon } from "../../components";
import useChatWidget from "../stores/chatWidget";
import useAuth from "../stores/auth";
import { regex } from "../../utilities/";

const $chatWidget = useChatWidget();
const $globalSetting = computed(() => $chatWidget.getGlobalSettings());
const $config = $chatWidget.getConfig;
const $setting = $chatWidget.getSettings;
const $payload = $chatWidget.getPayload();
const $auth = useAuth();
const formType = ref(null);
const loginFormRef = ref(null);
const registerFormRef = ref(null);

const validationCheck = (conditions) => {
  $auth.state.errors = {};
  let errors = 0;
  conditions.forEach((item) => {
    if (item.condition) {
      errors++;
      $auth.state.errors[item.field] = item.message;
    }
  });
  return {
    errors: errors > 0 ? true : false,
  };
};

const submitRegisterForm = async () => {
  const { errors } = validationCheck([
    {
      field: "first_name",
      condition: !$auth.registerForm.first_name,
      message: "First name is required",
    },
    {
      field: "last_name",
      condition: !$auth.registerForm.last_name,
      message: "Last name is required",
    },
    {
      field: "email",
      condition: !$auth.registerForm.email,
      message: "Email is required",
    },
    {
      field: "email",
      condition: !regex.email.test($auth.registerForm.email),
      message: "Invalid email format",
    },
    {
      field: "phone",
      condition: !$auth.registerForm.phone,
      message: "Phone is required",
    },
    {
      field: "phone",
      condition: !regex.phone.test($auth.registerForm.phone),
      message: "Invalid phone format",
    },
    {
      field: "password",
      condition: !$auth.registerForm.password,
      message: "Password is required",
    },
  ]);
  if (errors) return;

  if (!registerFormRef.value.checkValidity())
    return registerFormRef.value.reportValidity();

  const user = await $auth.submitRegisterForm();
};

const submitLoginForm = async () => {
  const { errors } = validationCheck([
    {
      field: "email",
      condition: !$auth.loginForm.email,
      message: "Email is required",
    },
    {
      field: "email",
      condition: !regex.email.test($auth.loginForm.email),
      message: "Invalid email format",
    },
    {
      field: "password",
      condition: !$auth.loginForm.password,
      message: "Password is required",
    },
  ]);
  if (errors) return;

  if (!loginFormRef.value.checkValidity())
    return loginFormRef.value.reportValidity();

  const user = await $auth.submitLoginForm();
};

const triggerForm = (type) => {
  formType.value = type;

  setTimeout(() => {
    const accountType = document.getElementById(
      "prebook-chat-component-account-" + type
    );

    accountType.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, 500);
};
</script>

<template>
  <div
    v-if="$globalSettings?.customer?.customer_id == undefined"
    id="prebook-chat-component-account"
    class="px-4 py-[15px]"
  >
    <div class="inline-flex w-[50px]">
      <img
        :src="$globalSetting?.settings?.business?.full_logo_url"
        :alt="$globalSetting?.settings?.business?.name"
        class="w-full object-contain"
      />
    </div>
    <h2 class="text-[20px]">
      {{ $setting.account.question }}
    </h2>
    <div class="flex flex-wrap gap-2 py-[10px]">
      <Button
        @click="triggerForm('login')"
        size="sm"
        type="secondary_gray"
        :class="formType === 'login' ? 'bg-gray-800 text-white' : ''"
        buttonStyle="padding: 12px 24px !important;"
      >
        <span class="font-normal text-[16px]">Login into my account</span>
      </Button>
      <Button
        @click="triggerForm('register')"
        size="sm"
        type="secondary_gray"
        :class="formType === 'register' ? 'bg-gray-800 text-white' : ''"
        buttonStyle="padding: 12px 24px !important;"
      >
        <span class="font-normal text-[16px]">Create new account</span>
      </Button>
    </div>

    <div
      id="prebook-chat-component-account-login"
      v-if="formType === 'login'"
      class=""
    >
      <form ref="loginFormRef" @submit.prevent="submitLoginForm">
        <div class="mt-2.5">
          <Label class="flex">
            Email
            <div class="text-red-500">*</div>
          </Label>
          <Input
            placeholder="mail@example.com"
            required
            v-model="$auth.loginForm.email"
            type="email"
            size="sm"
            inputContainerClass="py-2 px-2.5"
            inputMainContainerClass="mt-1"
            inputClass="border-none p-0"
            inputStyle="font-size:14px !important;"
          />
          <div v-if="$auth.state.errors.email" class="text-red-500 text-[14px]">
            {{ $auth.state.errors.email }}
          </div>
        </div>
        <div class="mt-2.5">
          <Label class="flex">
            Password
            <div class="text-red-500">*</div>
          </Label>
          <Input
            placeholder="Password"
            required
            v-model="$auth.loginForm.password"
            type="password"
            size="sm"
            inputContainerClass="py-2 px-2.5"
            inputMainContainerClass="mt-1"
            inputClass="border-none p-0"
            inputStyle="font-size:14px !important;"
          />
          <div
            v-if="$auth.state.errors.password"
            class="text-red-500 text-[14px]"
          >
            {{ $auth.state.errors.password }}
          </div>
        </div>
        <div class="mt-2.5">
          <Button
            @click="submitLoginForm"
            size="sm"
            radius="square"
            class="w-full border-[2px] bg-gray-800 hover:bg-gray-900 border-gray-800 font-medium focus:shadow-[0_0_0_4px_rgba(17,24,39,0.10)]"
            buttonStyle="padding: 10px 16px !important;"
          >
            {{ $auth.state.loading ? "Loading..." : "Login" }}
          </Button>
        </div>
      </form>
    </div>
    <div
      id="prebook-chat-component-account-register"
      v-if="formType === 'register'"
      class=""
    >
      <form ref="registerFormRef" @submit.prevent="submitRegisterForm">
        <div class="mt-2.5">
          <Label class="flex">
            First name
            <div class="text-red-500">*</div>
          </Label>
          <Input
            placeholder="First name"
            required
            v-model="$auth.registerForm.first_name"
            type="text"
            size="sm"
            inputContainerClass="py-2 px-2.5"
            inputMainContainerClass="mt-1"
            inputClass="border-none p-0"
            inputStyle="font-size:14px !important;"
          />
          <div
            v-if="$auth.state.errors.first_name"
            class="text-red-500 text-[14px]"
          >
            {{ $auth.state.errors.first_name }}
          </div>
        </div>
        <div class="mt-2.5">
          <Label class="flex">
            Last name
            <div class="text-red-500">*</div>
          </Label>
          <Input
            placeholder="Last name"
            required
            v-model="$auth.registerForm.last_name"
            type="text"
            size="sm"
            inputContainerClass="py-2 px-2.5"
            inputMainContainerClass="mt-1"
            inputClass="border-none p-0"
            inputStyle="font-size:14px !important;"
          />
          <div
            v-if="$auth.state.errors.last_name"
            class="text-red-500 text-[14px]"
          >
            {{ $auth.state.errors.last_name }}
          </div>
        </div>
        <div class="mt-2.5">
          <Label class="flex">
            Email
            <div class="text-red-500">*</div>
          </Label>
          <Input
            placeholder="mail@example.com"
            required
            v-model="$auth.registerForm.email"
            type="email"
            size="sm"
            inputContainerClass="py-2 px-2.5"
            inputMainContainerClass="mt-1"
            inputClass="border-none p-0"
            inputStyle="font-size:14px !important;"
          />
          <div v-if="$auth.state.errors.email" class="text-red-500 text-[14px]">
            {{ $auth.state.errors.email }}
          </div>
        </div>
        <!-- phone -->
        <div class="mt-2.5">
          <Label class="flex">
            Phone
            <div class="text-red-500">*</div>
          </Label>
          <Input
            placeholder="Phone"
            required
            v-model="$auth.registerForm.phone"
            type="text"
            size="sm"
            inputContainerClass="py-2 px-2.5"
            inputMainContainerClass="mt-1"
            inputClass="border-none p-0"
            inputStyle="font-size:14px !important;"
          />
          <div v-if="$auth.state.errors.phone" class="text-red-500 text-[14px]">
            {{ $auth.state.errors.phone }}
          </div>
        </div>
        <div class="mt-2.5">
          <Label class="flex">
            Password
            <div class="text-red-500">*</div>
          </Label>
          <Input
            placeholder="Password"
            required
            v-model="$auth.registerForm.password"
            type="password"
            size="sm"
            inputContainerClass="py-2 px-2.5"
            inputMainContainerClass="mt-1"
            inputClass="border-none p-0"
            inputStyle="font-size:14px !important;"
          />
          <div
            v-if="$auth.state.errors.password"
            class="text-red-500 text-[14px]"
          >
            {{ $auth.state.errors.password }}
          </div>
        </div>
        <div class="mt-2.5">
          <Button
            @click="submitRegisterForm"
            size="sm"
            radius="square"
            class="w-full border-[2px] bg-gray-800 hover:bg-gray-900 border-gray-800 font-medium focus:shadow-[0_0_0_4px_rgba(17,24,39,0.10)]"
            buttonStyle="padding: 10px 16px !important;"
          >
            {{ $auth.state.loading ? "Loading..." : "Sign up" }}
          </Button>
        </div>
      </form>
    </div>
  </div>
</template>
